import classNames from 'classnames';
import React, { FunctionComponent } from 'react';

import Typography from '@Components/Typography';
import { Messages } from '@Config/messages';
import { AuctionStatus } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import { formatDuration } from '@Utils/dates';
import { OptionalProps } from '@Utils/types';

import styles from './AuctionStatus.module.scss';

export interface AuctionStatusComponentProps {
  status: AuctionStatus;
  expirationDate: Date;
  className?: OptionalProps<typeof styles>;
  children?: React.ReactNode;
}

const getAuctionInfo = (expirationDate: Date, status: AuctionStatus) => {
  switch (status) {
    case AuctionStatus.Created:
      return {
        style: styles.waitingApproval,
        status: Messages.auctionWaitingForApprovalStatus,
      } as const;
    case AuctionStatus.Declined:
      return {
        style: styles.declined,
        status: Messages.auctionDeclinedStatus,
      } as const;
    case AuctionStatus.Ended:
      return {
        style: styles.ended,
        status: Messages.auctionEndedStatus,
      } as const;
    case AuctionStatus.Expired:
      return {
        style: styles.expired,
        status: Messages.auctionExpiredStatus,
      } as const;
    case AuctionStatus.Approved:
      return {
        style: styles.active,
        remainingTime: expirationDate && new Date(expirationDate).getTime() - new Date().getTime(),
        status: Messages.auctionActiveStatusNoExpiration,
        expirationMsg: Messages.auctionActiveStatusWithExpiration,
      } as const;
    default:
      return null;
  }
};

const AuctionStatusComponent: FunctionComponent<AuctionStatusComponentProps> = ({
  expirationDate,
  status,
  className,
  children,
}) => {
  const t = useTranslations();
  const statusInfo = getAuctionInfo(expirationDate, status);

  if (!statusInfo) {
    return null;
  }

  const statusText =
    statusInfo.remainingTime && statusInfo.remainingTime > 0 && statusInfo.expirationMsg
      ? t(statusInfo.expirationMsg, { value: formatDuration(statusInfo.remainingTime) })
      : t(statusInfo.status);

  return (
    <div className={classNames(styles.container, statusInfo.style, className?.container)}>
      <Typography className={styles.status} msg={statusText} tag="span" bold />
      {children}
    </div>
  );
};

export default AuctionStatusComponent;
